import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import reviewConstants from '../../../../../constants/review.constants';
import ErrorSnackbar from './ErrorSnackbar';
import HistoryTableRow from './HistoryTableRow';
import TableSkeleton from './TableSkeleton';

const TABLE_COLUMNS = [
  { id: 'email', name: 'Email', sortable: true },
  { id: 'department', name: 'Department', sortable: true },
  { id: 'reviewGoal', name: 'Review Goal', sortable: true },
  { id: 'reviewRecordLinks', name: 'Record', sortable: false },
  { id: 'newGoal', name: 'New Goal', sortable: true },
  { id: 'status', name: 'Status', sortable: true },
  { id: 'confirmed', name: 'Confirmed', sortable: true, align: 'right' },
  { id: 'declined', name: 'Declined', sortable: true, align: 'right' },
  { id: 'progress', name: 'Progress', sortable: true, align: 'center' },
  { id: 'updatedAt', name: 'Review Date', sortable: true },
];

function HistoryTable({
  isLoading,
  setIsLoading,
  reviewHistoryData,
  totalCount,
  currentPage,
  setCurrentPage,
  rowsPerPage,
  setRowsPerPage,
  setSort,
  fetchData,
  historySearchInput,
  historySort,
  template,
  skillStatistic,
}) {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('updatedAt');
  const [errorSnackbarText, setErrorSnackbarText] = useState(null);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
    setIsLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(parseInt(value, 10));
    setCurrentPage(0);
    setIsLoading(true);
  };

  useEffect(() => {
    if (orderBy) {
      setSort(order === 'asc' ? orderBy : `-${orderBy}`);
      setIsLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, orderBy]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getSortIcon = (columnId) => {
    if (orderBy !== columnId) {
      return <ArrowUpDown className="ml-1 h-3 w-3" />;
    }
    if (order === 'asc') {
      return <ArrowUp className="ml-1 h-3 w-3" />;
    }
    return <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const filteredReviewHistoryData = reviewHistoryData.filter(
    (reviewData) => reviewData.status !== reviewConstants.REVIEW_STATUSES.REVERTED,
  );

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12" />
            {TABLE_COLUMNS.map((column) => (
              <TableHead key={column.id} className={column.align === 'right' ? 'text-right' : ''}>
                {column.sortable ? (
                  <button
                    type="button"
                    className="inline-flex items-center hover:text-foreground transition-colors"
                    onClick={() => handleSort(column.id)}
                  >
                    {column.name}
                    {getSortIcon(column.id)}
                  </button>
                ) : (
                  column.name
                )}
              </TableHead>
            ))}
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading &&
            Array.from({ length: rowsPerPage }, (_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableSkeleton key={index} />
            ))}
          {!isLoading && filteredReviewHistoryData.length === 0 && (
            <TableRow>
              <TableCell colSpan={12} className="h-24 text-center text-muted-foreground">
                No review history found
              </TableCell>
            </TableRow>
          )}
          {!isLoading &&
            filteredReviewHistoryData.length > 0 &&
            filteredReviewHistoryData.map((history) => (
              <HistoryTableRow
                key={history._id}
                setIsLoading={setIsLoading}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                {...history}
                reviewHistoryData={reviewHistoryData}
                fetchData={fetchData}
                historySearchInput={historySearchInput}
                historySort={historySort}
                template={template}
                skillStatistic={skillStatistic}
                setErrorSnackbarText={setErrorSnackbarText}
              />
            ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div
        className="flex items-center justify-between px-4 py-3 border-t"
        style={{ borderColor: 'hsl(var(--border))' }}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => handleChangeRowsPerPage(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {currentPage * rowsPerPage + 1}-{Math.min((currentPage + 1) * rowsPerPage, totalCount)}{' '}
            of {totalCount}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleChangePage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleChangePage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <ErrorSnackbar message={errorSnackbarText} onClose={() => setErrorSnackbarText(null)} />
    </div>
  );
}

HistoryTable.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  reviewHistoryData: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      declined: PropTypes.number.isRequired,
      confirmed: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      updatedAt: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      avatar: PropTypes.shape({}).isRequired,
      department: PropTypes.string.isRequired,
      departmentName: PropTypes.string.isRequired,
      reviewGoal: PropTypes.string.isRequired,
      newGoal: PropTypes.string,
      progress: PropTypes.number.isRequired,
    }),
  ).isRequired,
  totalCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  historySearchInput: PropTypes.string.isRequired,
  historySort: PropTypes.string.isRequired,
  template: PropTypes.string.isRequired,
  skillStatistic: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
};

export default HistoryTable;
